// Manager landing — DESIGN §6.2.1 (PO Spec §1, 2026-06-21; card set
// revised PO 2026-08-06).
//
// THE FOUR CARDS ARE THE MANAGER'S MAIN PAGE AT EVERY WIDTH (PO
// 2026-08-06). Desktop used to land on the searchable pathway list
// with these cards hidden below `md`; that list now lives on
// `/manager/team`, which the fourth card points at. See
// `ManagerDashboard.tsx`.
//
// EVERY CARD WORKS ON AN EMPTY ACCOUNT, and that is the revision.
// The previous set derived its destinations from the most recent
// Open case, so a manager signing in for the first time — with no
// pathway, which is exactly when they least know what to say — got
// "Help me draft a response" pointing at the case-creation form
// with the subtitle "drafting opens once an employee is added", and
// "Show me the policy" pointing at `/manager` (a no-op link back to
// the page they were already on). The PO's note: drafting "needs to
// be generic and not tagged to anything. It should be a separate
// function in itself." So:
//
//   1. Draft a response  → `/manager/draft`     — case-less Sub-mode A′
//   2. Show me the policy → `/manager/policies` — org-scoped, no case
//   3. Bereavement guide  → `/manager/guide`    — static, no data at all
//   4. Support pathways   → `/manager/team`     — the list
//
// Only card 4 reads `cases`, and only for its subtitle counts.
//
// Spec greeting copy (PO §1.1) — "Hey [First Name], how can I help
// you today?" — gated on the JWT `name` claim (Phase 0.2) AND on
// the CGC sign-off per PROJECT §"Mobile-First Maeve Entry". The
// fallback below is neutral, anti-anthropomorphism-safe copy
// (DESIGN §1.4 #8) for a missing name: no first-person plural
// ("we"), no availability promise, no personalised address.

import { Link } from 'react-router';
import type { CaseSummary } from '@besideu/shared/cases';
import { MaeveAvatar } from '@/components/brand/MaeveAvatar.tsx';
import { ActionCard } from '@/components/ui/ActionCard.tsx';

export function ManagerMaeveFirst({
  cases,
  firstName,
  casesUnavailable = false,
}: {
  cases: CaseSummary[];
  firstName: string;
  /**
   * The pathway list failed to load. NOT the same as an empty list, and the
   * distinction is load-bearing: with the cards now the whole home page, a
   * failed fetch would otherwise render "No open support pathways" — telling
   * a manager who has an overdue touch point that they have nothing on. The
   * card says so instead, and still links through.
   */
  casesUnavailable?: boolean;
}) {
  // Read ONLY for the pathway card's counts and the return-to-work
  // continue link below — no card's destination depends on a case
  // existing any more. Cases come back sorted by createdAt desc.
  const draftTarget = cases.find((c) => c.status === 'Open');
  const overdueCount = cases.filter((c) => c.hasOverdueTouchpoint).length;
  const openCount = cases.filter((c) => c.status === 'Open').length;
  // PO Spec §1.3 — live counts of upcoming/overdue actions on the
  // "Current support pathways" card. "Overdue" surfaces in red; "due this
  // week" surfaces as a neutral count. The total is the open-case
  // count so a manager with no open cases sees a clean state.
  const dueThisWeek = cases.filter((c) => {
    if (c.status !== 'Open') return false;
    if (!c.returnToWorkDate) return false;
    const rtw = new Date(c.returnToWorkDate);
    const now = new Date();
    const daysUntil = Math.floor((rtw.getTime() - now.getTime()) / 86_400_000);
    return daysUntil >= 0 && daysUntil <= 7;
  }).length;

  return (
    <section className="space-y-8">
      <div className="flex flex-col items-center text-center">
        {/* §7 manager entry — role badge "Manager" in navy at 30%. */}
        <p className="text-xs font-semibold tracking-[2px] text-navy/30 uppercase">Manager</p>
        <MaeveAvatar size={140} className="mt-4" />
        <h1 className="mt-6 text-2xl font-bold tracking-tight text-foreground">
          {firstName ? `Hey ${firstName}, how can I help you today?` : 'Your team, at a glance.'}
        </h1>
        <p className="mt-2 text-base text-muted-foreground">Choose where to start.</p>
      </div>

      {/* Two columns at `sm`+ so four cards don't become a long scroll on a
          desktop. A grid, not a viewport branch — the tree is identical at
          every width (see `Shell.tsx` on why that matters here). */}
      <div className="grid gap-3 sm:grid-cols-2">
        {/* PO 2026-08-06 — the four teal action cards. Existing visual
            language uses brand teal via the `ActionCard` component. */}
        <ActionCard
          to="/manager/draft"
          title="Help me draft a response"
          subtitle="Get wording for a message you need to send. No pathway needed — this isn't tied to anyone."
          icon={
            <svg width="22" height="22" viewBox="0 0 24 24" focusable={false}>
              <path
                d="M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-5.4A8 8 0 1 1 21 12z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        />
        {/* Points at the org-wide Policies tab, NOT the per-case
            entitlements page. The case-scoped `/manager/cases/:id/policy`
            still exists and is still the right destination from inside a
            pathway (it answers "what is THIS person entitled to"); as a
            home card it silently answered a different question — whichever
            pathway happened to be most recent — and had no destination at
            all before the first one existed. */}
        <ActionCard
          to="/manager/policies"
          title="Show me the policy"
          subtitle="Your organisation's bereavement policy and leave entitlements."
          icon={
            <svg width="22" height="22" viewBox="0 0 24 24" focusable={false}>
              <path
                d="M4 10.5L12 4l8 6.5V20a1 1 0 0 1-1 1h-4v-6h-6v6H5a1 1 0 0 1-1-1V10.5z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
          }
        />
        {/* PO 2026-08-06 — "what they will need to do, return to work etc,
            this is just advice". Static content, no case, no Maeve call:
            see `@besideu/shared/bereavement-guide` for why. */}
        <ActionCard
          to="/manager/guide"
          title="Guide me through a bereavement experience"
          subtitle="What happens at each stage, from the first hours to the anniversary, and what you'll need to do."
          icon={
            <svg width="22" height="22" viewBox="0 0 24 24" focusable={false}>
              <path
                d="M4 5.5A1.5 1.5 0 0 1 5.5 4H10a2 2 0 0 1 2 2 2 2 0 0 1 2-2h4.5A1.5 1.5 0 0 1 20 5.5v12a1.5 1.5 0 0 1-1.5 1.5H14a2 2 0 0 0-2 2 2 2 0 0 0-2-2H5.5A1.5 1.5 0 0 1 4 17.5v-12zM12 6v14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        />
        {/* Hardcoded. This was a `touchpointsTo` prop back when the component
            had two call sites and one of them couldn't safely link at a
            UA-gated destination; the responsive rework left one call site, and
            PO 2026-08-06 left one correct destination, so the prop was pure
            indirection over a constant. */}
        <ActionCard
          to="/manager/team"
          title="Current support pathways"
          subtitle={
            casesUnavailable
              ? "Couldn't load your pathways just now — open the list to try again."
              : overdueCount > 0
                ? `${overdueCount} overdue, ${dueThisWeek} due this week across ${openCount} open support ${openCount === 1 ? 'pathway' : 'pathways'}.`
                : openCount > 0
                  ? `${openCount} open support ${openCount === 1 ? 'pathway' : 'pathways'} — ${dueThisWeek} due this week.`
                  : 'No open support pathways.'
          }
          icon={
            <svg width="22" height="22" viewBox="0 0 24 24" focusable={false}>
              <rect
                x="3.5"
                y="5"
                width="17"
                height="15"
                rx="2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M3.5 9.5h17M8 3.5v3M16 3.5v3"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          }
        />
      </div>

      {/* The PO's card set is four, and the fifth card it replaces was the
          Phase 4 §1 RTW deep-link — the one-tap route into the
          return-to-work plan form, which that spec made the priority-1
          mobile module. Demoted to a plain link rather than deleted: the
          four cards are the design, but silently costing a shipped
          priority-1 affordance two extra taps is a regression the note
          didn't ask for. FLAGGED FOR PO — delete this block if the answer
          is "four cards, nothing else". `?tab=plan` because the RTW form
          lives on the "Support plan" tab since the 2026-08-01 tab rework. */}
      {draftTarget ? (
        <p className="text-center text-sm text-muted-foreground">
          Picking up where you left off?{' '}
          <Link
            to={`/manager/cases/${draftTarget.id}?tab=plan`}
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            {draftTarget.employee.name}&rsquo;s return-to-work plan
          </Link>
        </p>
      ) : null}
    </section>
  );
}
